import { useMemo } from 'react';

import type {
  FindOrdersRequestFilter,
  FindOrdersRequestParams,
} from '~entities/order';
import { useFindOrdersQuery } from '~entities/order';
import type { Pos } from '~entities/pos';
import { isOnlinePos, useGetPointsOfSalesQuery } from '~entities/pos';

import { endOfDay, startOfDay, subDays } from 'date-fns';

import type { OrderTableData } from './orders-table-data';

type SuccessfulReturn = {
  isSuccess: true;
  data: { orders: OrderTableData[]; total: number };
};

type UnsuccessfulReturn = {
  isSuccess: false;
  data: undefined;
};

type DefaultReturn = {
  isLoading: boolean;
  isError: boolean;
};

type UseOrdersTableReturn = DefaultReturn &
  (SuccessfulReturn | UnsuccessfulReturn);

const defaultParams = {
  filter: {
    dateTo: endOfDay(new Date()).toISOString(),
    dateFrom: startOfDay(new Date()).toISOString(),
  },
  page: 0,
  perPage: 8,
};

export function useOrdersTableDataQuery(
  paginationParams: Pick<FindOrdersRequestParams, 'page' | 'perPage'>
): UseOrdersTableReturn {
  const posListQuery = useGetPointsOfSalesQuery();

  const onlinePosList = useMemo(
    () => posListQuery.data?.filter(isOnlinePos) ?? [],
    [posListQuery.data]
  );
  const onlineMerchantIds = new Set(onlinePosList.map((pos) => pos.merchantId));

  const filter: FindOrdersRequestFilter | undefined =
    onlinePosList.length > 0 && onlineMerchantIds.size > 0
      ? {
          merchantIds: [...onlineMerchantIds],
          pointOfSaleIds: onlinePosList.map((pos) => pos.id),
          dateTo: endOfDay(new Date()).toISOString(),
          dateFrom: endOfDay(subDays(new Date(), 8)).toISOString(),
        }
      : undefined;

  const params = filter ? { filter, ...paginationParams } : defaultParams;

  const ordersQuery = useFindOrdersQuery(params, {
    enabled: posListQuery.isSuccess,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    keepPreviousData: true,
  });

  const posMap = useMemo(
    () =>
      onlinePosList.reduce(
        (result, pos) => {
          result[pos.id] = pos;

          return result;
        },
        {} as Record<string, Pos>
      ) ?? {},
    [onlinePosList]
  );

  const queries = [posListQuery, ordersQuery];
  const isQueryLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const isFetching = queries.some((q) => q.isFetching);

  const isLoading =
    isQueryLoading || (isFetching && ordersQuery.isPreviousData);

  if (posListQuery.isSuccess && ordersQuery.isSuccess) {
    const { orders, total } = ordersQuery.data;
    const enrichedOrders: OrderTableData[] = orders.map((order) => {
      const posName = posMap[order.pointOfSalesId]?.name;

      return {
        ...order,
        posName,
      };
    });

    return {
      isSuccess: true,
      data: {
        orders: enrichedOrders,
        total,
      },
      isLoading,
      isError,
    };
  }

  return {
    isSuccess: false,
    data: undefined,
    isLoading,
    isError,
  };
}
